import React, { useState, useEffect } from 'react';
import { 
  Share2, 
  Copy, 
  Check, 
  Download, 
  MessageCircle, 
  Mail, 
  Smartphone,
  Facebook,
  Linkedin,
  Twitter,
  Link2,
  QrCode,
  Send,
  ExternalLink
} from 'lucide-react';

const ShareablePage = ({ 
  websiteUrl = "https://yusen-portfolio.preview.emergentagent.com",
  companyName = "Yusen Design & Contract Pte Ltd",
  tagline = "Interior Design & Renovation Specialist",
  description = "Transform your space with Singapore's trusted interior design and renovation experts. View our portfolio and get a free consultation."
}) => {
  const [copied, setCopied] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const [shareSupported, setShareSupported] = useState(false);

  useEffect(() => {
    setShareSupported(typeof navigator !== 'undefined' && !!navigator.share);
  }, []);

  const shareData = {
    title: companyName,
    text: description,
    url: websiteUrl
  };

  // Copy to clipboard
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(websiteUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = websiteUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // Native share (mobile)
  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (err) {
      // User cancelled the share dialog — expected behavior, no action needed
      if (err.name !== 'AbortError') {
        // Only unexpected errors reach here
      }
    }
    }
  };

  // Share via WhatsApp
  const shareWhatsApp = () => {
    const text = encodeURIComponent(`${description}\n\n${websiteUrl}`);
    window.open(`https://wa.me/?text=${text}`, '_blank');
  };

  // Share via Telegram
  const shareTelegram = () => {
    const text = encodeURIComponent(description);
    const url = encodeURIComponent(websiteUrl);
    window.open(`https://t.me/share/url?url=${url}&text=${text}`, '_blank');
  };

  // Share via Email
  const shareEmail = () => {
    const subject = encodeURIComponent(`Check out ${companyName}`);
    const body = encodeURIComponent(`${description}\n\nVisit: ${websiteUrl}`);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  // Share via SMS
  const shareSMS = () => {
    const text = encodeURIComponent(`${description} ${websiteUrl}`);
    window.location.href = `sms:?body=${text}`;
  };

  // Share via Facebook
  const shareFacebook = () => {
    const url = encodeURIComponent(websiteUrl);
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`, '_blank', 'width=600,height=400');
  };

  // Share via LinkedIn
  const shareLinkedIn = () => {
    const url = encodeURIComponent(websiteUrl);
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${url}`, '_blank', 'width=600,height=400');
  };

  // Share via Twitter/X
  const shareTwitter = () => {
    const text = encodeURIComponent(`${description}`);
    const url = encodeURIComponent(websiteUrl);
    window.open(`https://twitter.com/intent/tweet?text=${text}&url=${url}`, '_blank', 'width=600,height=400');
  };

  // Download QR Code
  const downloadQR = () => {
    const link = document.createElement('a');
    link.href = '/yusen-qr-branded-final.png';
    link.download = 'yusen-design-qr-code.png';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const displayUrl = websiteUrl.replace(/^https?:\/\//, '');

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#1E3A5F] rounded-xl flex items-center justify-center">
              <span className="text-white font-bold">Y</span>
            </div>
            <div>
              <h1 className="text-sm font-semibold text-gray-900">Share</h1>
              <p className="text-xs text-gray-500">{companyName}</p>
            </div>
          </div>
          <a 
            href={websiteUrl}
            className="text-[#1E3A5F] hover:text-blue-700 p-2 rounded-lg hover:bg-blue-50 transition-colors"
            aria-label="Visit website"
          >
            <ExternalLink className="w-5 h-5" />
          </a>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-8">
        {/* Preview Card */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden mb-8">
          {/* Preview Image/Banner */}
          <div className="h-32 bg-gradient-to-r from-[#1E3A5F] to-blue-700 relative overflow-hidden">
            <div className="absolute inset-0 opacity-10">
              <div className="absolute inset-0" style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23fff' fill-opacity='1'%3E%3Cpath d='M0 0h20v20H0V0zm10 17a7 7 0 1 0 0-14 7 7 0 0 0 0 14zm20 0a7 7 0 1 0 0-14 7 7 0 0 0 0 14zM10 37a7 7 0 1 0 0-14 7 7 0 0 0 0 14zm10-17h20v20H20V20zm10 17a7 7 0 1 0 0-14 7 7 0 0 0 0 14z'/%3E%3C/g%3E%3C/svg%3E")`,
              }} />
            </div>
            <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between">
              <div className="flex items-center gap-3">
                <div className="w-16 h-16 bg-white rounded-xl shadow-lg flex items-center justify-center border-2 border-white">
                  <span className="text-[#1E3A5F] text-xl font-bold">YD</span>
                </div>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-1">{companyName}</h2>
            <p className="text-[#1E3A5F] font-medium text-sm mb-3">{tagline}</p>
            <p className="text-gray-600 text-sm leading-relaxed mb-4">{description}</p>
            
            {/* URL Display */}
            <div className="flex items-center gap-2 bg-gray-50 rounded-xl p-3">
              <Link2 className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <span className="text-sm text-gray-600 truncate flex-1">{displayUrl}</span>
              <button
                onClick={handleCopy}
                className={`flex-shrink-0 p-2 rounded-lg transition-all duration-200 ${
                  copied 
                    ? 'bg-green-100 text-green-600' 
                    : 'bg-white text-gray-600 hover:bg-gray-100'
                }`}
                aria-label={copied ? 'Copied!' : 'Copy link'}
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </button>
            </div>
          </div>
        </div>

        {/* Native Share Button (Mobile) */}
        {shareSupported && (
          <button
            onClick={handleNativeShare}
            className="w-full bg-[#1E3A5F] hover:bg-blue-800 text-white font-semibold py-4 px-6 rounded-xl mb-6 flex items-center justify-center gap-3 shadow-lg shadow-blue-900/20 transition-all duration-200 active:scale-[0.98]"
            data-testid="native-share-btn"
          >
            <Share2 className="w-5 h-5" />
            Share
          </button>
        )}

        {/* Share Options Grid */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <h3 className="text-sm font-semibold text-gray-900 mb-4">Share via</h3>
          
          <div className="grid grid-cols-4 gap-4">
            {/* WhatsApp */}
            <button
              onClick={shareWhatsApp}
              className="flex flex-col items-center gap-2 p-3 rounded-xl hover:bg-gray-50 transition-colors group"
              data-testid="share-whatsapp"
            >
              <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                <MessageCircle className="w-6 h-6 text-white" />
              </div>
              <span className="text-xs text-gray-600">WhatsApp</span>
            </button>

            {/* Telegram */}
            <button
              onClick={shareTelegram}
              className="flex flex-col items-center gap-2 p-3 rounded-xl hover:bg-gray-50 transition-colors group"
              data-testid="share-telegram"
            >
              <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                <Send className="w-6 h-6 text-white" />
              </div>
              <span className="text-xs text-gray-600">Telegram</span>
            </button>

            {/* SMS */}
            <button
              onClick={shareSMS}
              className="flex flex-col items-center gap-2 p-3 rounded-xl hover:bg-gray-50 transition-colors group"
              data-testid="share-sms"
            >
              <div className="w-12 h-12 bg-emerald-500 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                <Smartphone className="w-6 h-6 text-white" />
              </div>
              <span className="text-xs text-gray-600">SMS</span>
            </button>

            {/* Email */}
            <button
              onClick={shareEmail}
              className="flex flex-col items-center gap-2 p-3 rounded-xl hover:bg-gray-50 transition-colors group"
              data-testid="share-email"
            >
              <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                <Mail className="w-6 h-6 text-white" />
              </div>
              <span className="text-xs text-gray-600">Email</span>
            </button>

            {/* Facebook */}
            <button
              onClick={shareFacebook}
              className="flex flex-col items-center gap-2 p-3 rounded-xl hover:bg-gray-50 transition-colors group"
              data-testid="share-facebook"
            >
              <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                <Facebook className="w-6 h-6 text-white" />
              </div>
              <span className="text-xs text-gray-600">Facebook</span>
            </button>

            {/* LinkedIn */}
            <button
              onClick={shareLinkedIn}
              className="flex flex-col items-center gap-2 p-3 rounded-xl hover:bg-gray-50 transition-colors group"
              data-testid="share-linkedin"
            >
              <div className="w-12 h-12 bg-blue-700 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                <Linkedin className="w-6 h-6 text-white" />
              </div>
              <span className="text-xs text-gray-600">LinkedIn</span>
            </button>

            {/* Twitter/X */}
            <button
              onClick={shareTwitter}
              className="flex flex-col items-center gap-2 p-3 rounded-xl hover:bg-gray-50 transition-colors group"
              data-testid="share-twitter"
            >
              <div className="w-12 h-12 bg-black rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                <Twitter className="w-6 h-6 text-white" />
              </div>
              <span className="text-xs text-gray-600">X</span>
            </button>

            {/* Copy Link */}
            <button
              onClick={handleCopy}
              className="flex flex-col items-center gap-2 p-3 rounded-xl hover:bg-gray-50 transition-colors group"
              data-testid="share-copy"
            >
              <div className={`w-12 h-12 rounded-full flex items-center justify-center group-hover:scale-110 transition-all ${
                copied ? 'bg-green-500' : 'bg-gray-600'
              }`}>
                {copied ? <Check className="w-6 h-6 text-white" /> : <Copy className="w-6 h-6 text-white" />}
              </div>
              <span className="text-xs text-gray-600">{copied ? 'Copied!' : 'Copy'}</span>
            </button>
          </div>
        </div>

        {/* QR Code Section */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-900">QR Code</h3>
            <button
              onClick={() => setShowQR(!showQR)}
              className="text-[#1E3A5F] text-sm font-medium hover:underline"
            >
              {showQR ? 'Hide' : 'Show'}
            </button>
          </div>

          {showQR && (
            <div className="flex flex-col items-center animate-in slide-in-from-top-2 fade-in duration-300">
              {/* QR Code Image */}
              <div className="bg-white p-4 rounded-xl shadow-inner border border-gray-100 mb-4">
                <img
                  src="/yusen-qr-branded-final.png"
                  alt="QR Code for Yusen Design website"
                  className="w-48 h-48 object-contain"
                />
              </div>

              {/* Download Button */}
              <button
                onClick={downloadQR}
                className="flex items-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-3 px-6 rounded-xl transition-colors"
                data-testid="download-qr"
              >
                <Download className="w-4 h-4" />
                Download QR Code
              </button>

              <p className="text-xs text-gray-500 mt-3 text-center">
                Save and share this QR code for others to scan
              </p>
            </div>
          )}

          {!showQR && (
            <button
              onClick={() => setShowQR(true)}
              className="w-full flex items-center justify-center gap-3 bg-gray-50 hover:bg-gray-100 text-gray-600 py-4 rounded-xl transition-colors"
            >
              <QrCode className="w-5 h-5" />
              <span className="font-medium">View QR Code</span>
            </button>
          )}
        </div>

        {/* Visit Website CTA */}
        <a
          href={websiteUrl}
          className="block w-full bg-gradient-to-r from-[#1E3A5F] to-blue-700 hover:from-blue-800 hover:to-blue-800 text-white font-semibold py-4 px-6 rounded-xl text-center shadow-lg shadow-blue-900/20 transition-all duration-200 active:scale-[0.98]"
          data-testid="visit-website-btn"
        >
          Visit Website
        </a>

        {/* Footer */}
        <footer className="mt-8 text-center">
          <p className="text-gray-400 text-xs">
            © 2024 {companyName}. All rights reserved.
          </p>
        </footer>
      </main>

      {/* Copied Toast Notification */}
      <div
        className={`fixed bottom-6 left-1/2 -translate-x-1/2 bg-gray-900 text-white px-6 py-3 rounded-full shadow-xl transition-all duration-300 flex items-center gap-2 ${
          copied ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'
        }`}
      >
        <Check className="w-4 h-4 text-green-400" />
        Link copied to clipboard!
      </div>
    </div>
  );
};

export default ShareablePage;
